import { IKWFRoute } from '../KWFMain';
import { RWFDocViewer, IRWF3DocLink } from '@common/preview/preview.shared';
import { CompatibilityService } from '@common/services/compatibilityservice';
import { IsExternalControlled, SPAProxyController } from '../rwf.spa.proxy';
import { AsObservableArray, ShowOverlay } from '@common/navkendo';
import { IDocLink } from '@common/preview/preview.d';
import { fn } from '@common/utils';
import { GetFileIcon } from '../KWFUtils';
import { IFDRW_Workflow_Running_Instances, GetAsDoclink, Type_10, GetSortedDocs, IRwfBootstrapParameters, PreviewType, ResolvePreviewSettings } from '@common/rwf';
import { TNavUrlAccess } from '@common/navdata';
import { CtrlDocumentInfo } from '../documentinfo/CtrlDocumentInfo';
import { IPreviewSettings } from '@common/interfaces';


export const RWFPreviewControllerCreated = "RWFPreviewControllerCreated";
export const PreviewInit = "PreviewInit";
export const PreviewConfig = "PreviewConfig";

export interface IPreviewConfig {
    RowItem: kendo.data.ObservableObject | any,
    Title: string, 
    ExtDocUrls: IDocLink[],
    DisplayGroups: { Label: string, Value: string }[][],
    PreviewUrl?: string
}

export class RWFPreviewController {
    static RouteName = "extpreview";
    static Route: IKWFRoute = {
        HideBar: true,
        template: "<rwfpreview></rwfpreview>",
    };
    static Directive = [(): ng.IDirective => {
        return <ng.IDirective>{
            controller: RWFPreviewController,
            controllerAs: "Preview",
            template: require("./rwfpreview.html"),
            link: (scope, element, attributes, controller: RWFPreviewController) => {
                window.setTimeout(() => { // Problem: we need child element to be created!
                    var PreviewSettings = controller.PreviewSettings
                    var previewContainer = $("#DataGridPreviewContainer", element)[0];
                    if (!previewContainer) {
                        throw new Error("PreviewContainer!");
                    }
                    controller._previewControl = new RWFDocViewer(previewContainer, PreviewSettings);
                    controller.SelectedUrlChanged(controller.SelectedUrl);
                    scope.$emit(RWFPreviewControllerCreated, controller);
                });
            }
        };
    }];

    static $inject = ["$scope", "CompatibilityService"];
    constructor(private $scope: ng.IScope, private $c: CompatibilityService) {
        const $rwf = $c.resolve("Rwf");
        const $RwfBootstrap = $rwf("RwfBootstrap");
        $RwfBootstrap.OnResolved((rwfBootstrap: IRwfBootstrapParameters) => {
            let settings = ResolvePreviewSettings(rwfBootstrap, this.PreviewSettings);
            let pType = settings.PreviewType;
            this.IsPluginPreview = pType == PreviewType.Standard || pType == PreviewType.Browser;
            return this;
        }, true);

        const mainProxy = this.IsExternalPreview = !!IsExternalControlled();
        if (mainProxy) {

            $scope.$on("$destroy",
                SPAProxyController.OnMessage(PreviewConfig, (data) => this.SetCurrentData2Local(data)));

            var t = $scope.$watch(() => this._previewControl && (window.opener as Window)?.UndockedCallbacks?.ExternalPreview, (cb) => {
                if (cb) {
                    t();
                    $RwfBootstrap.Resolve.then(() => {
                        cb.trigger(PreviewInit, {});
                    });
                }
            });
        }

        $scope.$watch(() => this.SelectedUrl, selected => this.SelectedUrlChanged(selected));

        // Is Single Instance
        $(window).on(ShowOverlay, () => {
            if (this.IsPluginPreview) {
                // A short hide and show will workaround the PDF plugin overlay on popups
                $("#DataGridPDFPreview").hide(0).show(0);
            }
        });
    }


    public readonly PreviewSettings: IPreviewSettings = {
        PreviewType: PreviewType.Standard,
        annotationUser: "unbekannt",
        enableReadOnlyMode: true
        // MSQQQ relativePath: "./"
    };

    /** Preview via browser plugins? */
    public IsPluginPreview: boolean;

    public readonly IsExternalPreview: boolean;


    _previewControl: RWFDocViewer;
    /** DisplayGroups */
    public DisplayGroups: { Label: string, Value: string }[][];
    /** Current Display DocUrls */
    public readonly DocUrls = AsObservableArray<IDocLink>([]);

    public Loading: boolean;

    public DocumentLoading: boolean;

    public Error: String;

    private SelectedUrlChanged(selected: IDocLink) {
        const docUrls = this.DocUrls || [];
        const $c = this.$c;
        var idx = docUrls.indexOf(selected);
        // Refresh Action State
        this.OpPrevious.Enabled = idx > 0;
        this.OpNext.Enabled = idx >= 0 && idx < docUrls.length - 1;
        this.OpOpenExternal.Enabled = !!this.SelectedUrl;
        this.updateExtensionIcon();
        if (this._previewControl) { // if has Url or was already shown ...
            this.DocumentLoading = true;
            this._previewControl.element.css("height", docUrls.length > 1 ? "calc(100% - 54px)" : "100%");
            $c.finally($c.when(this._previewControl.SetDocLink(selected)), () => this.DocumentLoading = false);
        }
    }

    /** Currently Selected Url */
    public get SelectedUrl() {
        return this._SelectedUrl;
    }
    public set SelectedUrl(val: IDocLink) {
        if (this._SelectedUrl !== val) {
            this._SelectedUrl = val;
        }
    }
    public _SelectedUrl: IDocLink;

    /** Corresponding icon style to SelectedUrl.Extension */
    public SelectedIconStyle: string;


    public SetCurrentData2Local(config: IPreviewConfig) {
        var rowitem = config.RowItem;
        this.DisplayGroups = config.DisplayGroups || [];

        if (config.RowItem) {

            if(this.IsExternalPreview) {
                document.title = config.Title;
            }
            // Reset state before reload
            this.Reset(false);

            var resolveDocUrls = () => {
                if (config.ExtDocUrls?.length > 0)
                    return this.$c.when(config.ExtDocUrls);

                return GetDocLinks(this.$c, rowitem, this.$c.resolve("NavUrl"));
            };

            const resetLoading = () => this.Loading = false;
            // Resolve doc url
            resolveDocUrls().then(docLinks => {
                // Update doc navi
                var docUrls = this.DocUrls;
                docUrls.splice(0, docUrls.length);
                docUrls.push(...docLinks);
                this.SelectedUrl = docUrls.filter(v => v.Url === config.PreviewUrl).pop() || docUrls[0];

                // Preview first doc (sorted in GetDocLinks)
                if (docUrls.length == 0) {
                    throw new Error("Keine Dokumente gefunden");
                }
            }).then(undefined, err => {
                var errInfo = fn.resolveToError(err);
                this.Error = errInfo || "Fehler";
            }).then(resetLoading, resetLoading);
        } else {
            this.Reset(true);
        }
    }

    Reset(full: boolean) {
        if (full) {
            this.DocUrls.splice(0, this.DocUrls.length);
            this.SelectedUrl = null;
            if(this.IsExternalPreview) {
                document.title = 'Vorschau';
            }    
        }
        this.Error = undefined;
        this.Loading = true;

        if (!this.$scope.$root.$$phase)
            this.$scope.$apply();
    }

    private updateExtensionIcon = () => {
        var extension = this.SelectedUrl && this.SelectedUrl.Extension;
        if (extension && extension.toLowerCase() !== "pdf") {
            this.SelectedIconStyle = 'url("' + GetFileIcon(extension) + '")';
        } else {
            this.SelectedIconStyle = '';
        }
    }

    /** Operation : Move to Next Url */
    public OpNext = {
        Execute: () => {
            var docUrls = this.DocUrls,
                idx = docUrls.indexOf(this.SelectedUrl);
            if (idx >= 0 && idx < docUrls.length - 1) {
                this.SelectedUrl = docUrls[idx + 1];
            }
        },
        Enabled: true
    };

    /** Operation : Move to Previous Url */
    public OpPrevious = {
        Execute: () => {
            var docUrls = this.DocUrls,
                idx = docUrls.indexOf(this.SelectedUrl);
            if (idx > 0) {
                this.SelectedUrl = docUrls[idx - 1];
            }
        },
        Enabled: true,
    };

    /** Operation Open current Selected Url */
    public OpOpenExternal = {
        Execute: () => {
            var selectedDoc = this.SelectedUrl;
            if (selectedDoc) {
                window.open(selectedDoc.Url, '_blank');
            }
        },
        Enabled: true
    };
}


/** Return all docs */
export function GetDocLinks(this: void, $c: CompatibilityService, item: any, navUrl_?: TNavUrlAccess) {
    return $c.resolve("NavUrl").ResolveUrl(navUrl_, true).then(navUrlInfo => {
        let isRunning = item && item.ID,
            dummy: IRWF3DocLink,
            navUrl = navUrlInfo.FullUrl,
            isArchived = item && item.WorkflowID;

        if (isRunning) {
            const asRunning: IFDRW_Workflow_Running_Instances = item,
                // Convention: show main doc only on Involved ( TODO : Must be handled in NAV!)
                mainDocOnly = (asRunning.Involved === true) && !(asRunning.MyDocument || asRunning.InvoiceLegder);

            return $c.resolve("NavClient").Read({
                PageName: "FDRW_WFInstance",
                NavUrl: navUrl,
                Keys: { ID: item.ID }
            }, true).then(wfInstanceResult => {
                var documents = wfInstanceResult.Data.Workflow_Documents_Sub;
                if (mainDocOnly) {
                    documents = documents.filter(item => item.Type == Type_10.Main_Document);
                }
                // Get doc
                return GetSortedDocs(documents || []).map(doc => GetAsDoclink(doc, navUrl));
            });
        }
        else if (isArchived) {
            return CtrlDocumentInfo.GetArchiveDocumentsInfo($c.resolve("Rwf"), item);
            // return [dummy = {
            //     ID: undefined,
            //     WorkflowInstanceID: item.WorkflowID,
            //     NavUrl: navUrl,
            //     IsArchived: true,
            //     Title: "Hauptdokument",
            //     Url: GetKWF3ArchivedDocumentUrl(item.WorkflowID, null, navUrl),
            //     Extension: "pdf"
            // }];
        }
        else {
            // Should not happen
            return [];
        }
    });
}
